// Deep merge utility compatible with Edge Runtime, similar to Lodash merge
/**
 * Deeply merges multiple source objects into a target object, similar to Lodash merge.
 * Arrays are replaced entirely (like Lodash merge), and function properties are handled properly.
 * @param sources The source objects to merge from.
 * @returns The merged object.
 */

export function deepMerge<T>(...sources: T[]): T {
  // Early return if no sources to merge
  if (sources.length === 0) return {} as T;

  // Always create a fresh result object to avoid mutating any argument
  const result = Array.isArray(sources[0]) ? [] : {};

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    // Skip null/undefined sources
    if (!source || typeof source !== 'object') continue;

    const keys = Object.keys(source);
    const keysLength = keys.length;

    for (let j = 0; j < keysLength; j++) {
      const key = keys[j];
      const sourceValue = (source as Record<string, unknown>)[key];
      const targetValue = (result as Record<string, unknown>)[key];

      // Handle arrays (replace entirely like Lodash)
      if (Array.isArray(sourceValue)) {
        (result as Record<string, unknown>)[key] = [...sourceValue];
        continue;
      }

      // Handle objects (deep merge)
      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        (result as Record<string, unknown>)[key] = deepMerge(
          targetValue &&
            typeof targetValue === 'object' &&
            !Array.isArray(targetValue)
            ? targetValue
            : {},
          sourceValue,
        );
        continue;
      }

      // Handle primitives and functions (overwrite)
      (result as Record<string, unknown>)[key] = sourceValue;
    }
  }

  return result as T;
}
